import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { analytics, schools } from '@/lib/api';
import { getStoredUser } from '@/lib/auth';
import { format, parseISO } from 'date-fns';
import { 
  ArrowLeft, ArrowUp, Clock, Loader2, BarChart3, TrendingUp, 
  Users, CheckCircle, XCircle, Settings
} from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';
import PlanLimitTooltip from '@/components/PlanLimitTooltip';
import { usePlanLimits } from '@/hooks/usePlanLimits';

export default function Analytics() {
  const { schoolId } = useParams();
  const navigate = useNavigate();
  const { planHasFeature, upgradeTooltipMessage, isLoading: planLimitsLoading } = usePlanLimits();
  const [user] = useState(getStoredUser());
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [school, setSchool] = useState(null);
  const [subPositions, setSubPositions] = useState({});

  const isEmployer = user?.role === 'employer';
  const hasFullAnalytics = isEmployer && planHasFeature(school?.plan_id, 'full_analytics');

  useEffect(() => {
    // Wait for the plan catalog before deciding whether to request the
    // gated position-history endpoint at all.
    if (isEmployer && planLimitsLoading) return;

    const fetchData = async () => {
      try {
        if (isEmployer && schoolId) {
          const [analyticsRes, schoolRes] = await Promise.all([
            analytics.getEmployerAnalytics(schoolId),
            schools.get(schoolId)
          ]);
          setData(analyticsRes.data);
          setSchool(schoolRes.data);

          // Basic totals (above) always render; the detailed per-Sub position
          // history is a Primary+ feature — don't even request it on a plan
          // without full_analytics, rather than requesting and letting the
          // server's require_feature("full_analytics") 402 it.
          if (planHasFeature(schoolRes.data?.plan_id, 'full_analytics')) {
            const posHistoryRes = await analytics.getSubPositionHistory(schoolId).catch(() => ({ data: {} }));
            setSubPositions(posHistoryRes.data || {});
          }
        } else {
          const analyticsRes = await analytics.getTOCAnalytics();
          setData(analyticsRes.data);
        }
      } catch (_e) {
        toast.error('Failed to load analytics');
        navigate(isEmployer ? '/employer' : '/toc');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [schoolId, isEmployer, navigate, planLimitsLoading, planHasFeature]);
  
  // Scroll to sub detail if hash is present
  useEffect(() => {
    if (!isLoading && window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
      }
    }
  }, [isLoading]);
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const scrollToSub = (tocId) => {
    const el = document.getElementById(`sub-detail-${tocId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  if (isLoading || (isEmployer && planLimitsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" id="analytics-top">
      {/* Header */}
      <header className={`${isEmployer ? 'bg-slate-900' : 'bg-emerald-600'} text-white`}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to={isEmployer ? '/employer' : '/toc'}>
            <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <BrandLogo variant={isEmployer ? "on-dark-blue" : "on-green"} size="lg" />
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <BarChart3 className="h-6 w-6 text-slate-600" />
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
              Analytics
            </h1>
          </div>
          <p className="text-slate-600">
            {isEmployer 
              ? `Sub performance for ${school?.name}`
              : 'Your performance across all connected schools'
            }
          </p>
        </div>
        
        {isEmployer ? (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Users className="h-4 w-4" />
                    <span className="text-sm">Total Subs</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{data.length}</p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <TrendingUp className="h-4 w-4" />
                    <span className="text-sm">Avg. Accept Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-600">
                    {data.length > 0 
                      ? Math.round(data.reduce((acc, t) => acc + t.acceptance_rate, 0) / data.length)
                      : 0
                    }%
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Total Accepted</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {data.reduce((acc, t) => acc + t.accepted, 0)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <XCircle className="h-4 w-4" />
                    <span className="text-sm">Total Declined</span>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {data.reduce((acc, t) => acc + t.declined, 0)}
                  </p>
                </CardContent>
              </Card>
            </div>
            
            {/* Sub Performance Summary Table */}
            {data.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No Sub data yet</h3>
                  <p className="text-sm text-slate-500">
                    Analytics will appear once you have Subs connected and positions posted
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200 mb-12">
                <CardHeader>
                  <CardTitle className="text-lg font-['Manrope']">Sub Performance</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sub Name</TableHead>
                        <TableHead className="text-center">Offered</TableHead>
                        <TableHead className="text-center">Accepted</TableHead>
                        <TableHead className="text-center">Declined</TableHead>
                        <TableHead className="text-center">No Response</TableHead>
                        <TableHead>Accept Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.map((toc) => (
                        <TableRow key={toc.toc_id}>
                          <TableCell>
                            <button
                              className="text-left hover:underline focus:outline-none"
                              onClick={() => scrollToSub(toc.toc_id)}
                              data-testid={`sub-name-link-${toc.toc_id}`}
                            >
                              <p className="font-medium text-blue-700 hover:text-blue-900">{toc.toc_name}</p>
                              <p className="text-xs text-slate-500">{toc.toc_email}</p>
                            </button>
                          </TableCell>
                          <TableCell className="text-center">{toc.total_offered}</TableCell>
                          <TableCell className="text-center text-emerald-600 font-medium">{toc.accepted}</TableCell>
                          <TableCell className="text-center text-rose-600 font-medium">{toc.declined}</TableCell>
                          <TableCell className="text-center text-slate-500">{toc.no_response}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={toc.acceptance_rate} className="h-2 flex-1" />
                              <span className="text-sm font-medium text-slate-700 w-12">
                                {toc.acceptance_rate}%
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
            
            {/* Per-Sub Position Detail Tables — full_analytics tier only */}
            {!hasFullAnalytics ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 flex flex-col items-center text-center gap-3">
                  <BarChart3 className="h-12 w-12 text-slate-300" />
                  <div>
                    <h3 className="text-lg font-semibold text-slate-700 mb-1">Detailed Position History</h3>
                    <p className="text-sm text-slate-500 max-w-md">
                      Per-Sub position history is part of full analytics, available on Primary and above.
                    </p>
                  </div>
                  <PlanLimitTooltip
                    variant="outline"
                    disabled
                    message={upgradeTooltipMessage('full_analytics')}
                    data-testid="full-analytics-locked"
                  >
                    View Detailed History
                  </PlanLimitTooltip>
                </CardContent>
              </Card>
            ) : data.map((toc) => {
              const positions = subPositions[toc.toc_id] || [];
              return (
                <div key={toc.toc_id} id={`sub-detail-${toc.toc_id}`} className="mb-10 scroll-mt-4">
                  <Card className="border-slate-200">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <CardTitle className="text-lg font-['Manrope']">
                          {toc.toc_name}
                        </CardTitle>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={scrollToTop}
                            data-testid={`back-to-top-${toc.toc_id}`}
                          >
                            <ArrowUp className="h-3.5 w-3.5 mr-1" />
                            Back to top
                          </Button>
                          <Link to={`/employer/school/${schoolId}/tocs#sub-${toc.toc_id}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              data-testid={`manage-sub-${toc.toc_id}`}
                            >
                              <Settings className="h-3.5 w-3.5 mr-1" />
                              Manage this Sub
                            </Button>
                          </Link>
                        </div>
                      </div>
                      <p className="text-sm text-slate-500">{toc.toc_email}</p>
                    </CardHeader>
                    <CardContent className="p-0">
                      {positions.length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500">
                          No positions offered to this Sub yet
                        </div>
                      ) : (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Date(s)</TableHead>
                              <TableHead>Category</TableHead>
                              <TableHead>Time</TableHead>
                              <TableHead className="text-center">Response</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {positions.map((pos) => {
                              const dates = pos.dates || [];
                              let dateDisplay;
                              if (dates.length === 0) {
                                dateDisplay = '—';
                              } else if (dates.length === 1) {
                                dateDisplay = format(parseISO(dates[0]), 'MMM d, yyyy');
                              } else {
                                dateDisplay = dates.map(d => format(parseISO(d), 'MMM d')).join(', ');
                              }
                              
                              return (
                                <TableRow key={pos.position_id}>
                                  <TableCell className="text-sm">{dateDisplay}</TableCell>
                                  <TableCell className="text-sm">
                                    {pos.grade_level || '—'}
                                  </TableCell>
                                  <TableCell className="text-sm text-slate-600">
                                    {pos.start_time} - {pos.end_time}
                                  </TableCell>
                                  <TableCell className="text-center">
                                    {pos.response === 'accept' && (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                                        Accepted
                                      </Badge>
                                    )}
                                    {pos.response === 'decline' && (
                                      <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                                        Declined
                                      </Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      )}
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </>
        ) : (
          /* TOC View - School Analytics (unchanged) */
          <>
            {data.length === 0 ? (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <BarChart3 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">No analytics yet</h3>
                  <p className="text-sm text-slate-500">
                    Connect to schools and respond to positions to see your stats
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6 md:grid-cols-2">
                {data.map((school) => (
                  <Card key={school.school_id} className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg font-['Manrope']">{school.school_name}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-slate-900">{school.total_positions}</p>
                          <p className="text-xs text-slate-500">Positions Offered</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-emerald-600">{school.filled_by_me}</p>
                          <p className="text-xs text-slate-500">Jobs Filled</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Accepted</span>
                          <span className="font-medium text-emerald-600">{school.accepted}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">Declined</span>
                          <span className="font-medium text-rose-600">{school.declined}</span>
                        </div>
                        <div className="pt-2">
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-600">Accept Rate</span>
                            <span className="font-medium">
                              {school.total_positions > 0 
                                ? Math.round((school.accepted / school.total_positions) * 100)
                                : 0
                              }%
                            </span>
                          </div>
                          <Progress 
                            value={school.total_positions > 0 
                              ? (school.accepted / school.total_positions) * 100
                              : 0
                            } 
                            className="h-2"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </main>
      <AppFooter role={isEmployer ? "employer" : "toc"} />
    </div>
  );
}
